import React, { useCallback, useEffect, useState } from 'react';
import { listPendingManualPayments, confirmManualPayment } from '../../api/payments';
import { formatDateTime } from '../../utils/format';

function money(cents, currency) {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' })
      .format((cents || 0) / 100);
  } catch {
    return `${currency || 'USD'} ${((cents || 0) / 100).toFixed(2)}`;
  }
}

export default function PendingPaymentsPage() {
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await listPendingManualPayments(page);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not load pending payments.');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const approve = async (payment) => {
    if (busyId) return;
    const confirmed = window.confirm(
      `Confirm you received ${money(payment.amountCents, payment.currency)} from ${payment.organizationName}? ` +
      `This will grant ${payment.credits ?? 'the pack'} credits. Payment ID: ${payment.id}`,
    );
    if (!confirmed) return;
    setBusyId(payment.id);
    setError('');
    setNotice('');
    try {
      await confirmManualPayment(payment.id);
      setNotice(`Payment ${payment.id} confirmed. Credits granted to ${payment.organizationName}.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not confirm payment.');
    } finally {
      setBusyId(null);
    }
  };

  const rows = data?.content || [];
  return (
    <div className="page-pad page-y max-w-[1400px] mx-auto w-full space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">Platform admin</p>
          <h1 className="font-heading text-2xl font-bold text-ink tracking-tight">Pending payments</h1>
          <p className="text-sm text-ink-faint mt-1">Confirm only after the manual payment has actually arrived. Confirmation grants credits immediately.</p>
        </div>
        <button type="button" onClick={load} disabled={loading || !!busyId}
          className="px-3 py-2 text-sm border border-line rounded-lg disabled:opacity-50">Refresh</button>
      </div>
      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {notice && <div role="status" className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-800">{notice}</div>}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas border-b border-line">
            <tr>
              {['Organization', 'Credits', 'Amount', 'Created', 'Payment ID', 'Action'].map(label =>
                <th key={label} className="px-4 py-3 text-left text-xs font-semibold text-ink-faint uppercase tracking-wider">{label}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft">
            {loading ? <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-faint">Loading payments…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-faint">No pending manual payments.</td></tr>
                : rows.map(payment => <tr key={payment.id}>
                  <td className="px-4 py-3 font-semibold text-ink">{payment.organizationName}<div className="text-xs font-normal text-ink-faint">{payment.orgId}</div></td>
                  <td className="px-4 py-3">{payment.credits ?? '—'}</td>
                  <td className="px-4 py-3 font-semibold">{money(payment.amountCents, payment.currency)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(payment.createdAt)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{payment.id}</td>
                  <td className="px-4 py-3">
                    <button type="button" disabled={!!busyId || loading} onClick={() => approve(payment)}
                      className="rounded-lg bg-teal-600 px-3 py-2 text-white font-semibold disabled:opacity-50 whitespace-nowrap">
                      {busyId === payment.id ? 'Confirming…' : 'Mark paid & grant credits'}
                    </button>
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-end gap-3 text-sm">
        <button type="button" disabled={page === 0 || loading} onClick={() => setPage(p => p - 1)}>Previous</button>
        <span>Page {page + 1}</span>
        <button type="button" disabled={loading || !data || data.last} onClick={() => setPage(p => p + 1)}>Next</button>
      </div>
    </div>
  );
}
